import { CreateDashboardDefinitionBase } from '../../../_shared/dashboard-definition.model';
import { axesView, composeHeading, createRouteMethodologyFilter, defineDashboard, isKEur, resolveProduct } from '../../../_shared/dashboard-axes';
import { createTimeFilters, createPeriodLabelTransformers, createPeriodYearTransformers, resolveTimeFilter, timeFilterDetail } from '../../../_shared/time-filters';
import {
  ChartFieldDashboardDataTransformer,
  NumDEDashboardDataTransformer,
  PercentageValueDataTransformer,
  RankDashboardDataTransformer,
  UndefinedStateToSumGermanyDashboardDataTransformer,
} from '../../../../data_transformers/dashboard_data_transformer';
import {
  DashboardRankingMultiColumnDefinition,
  RANKING_MC_RANK_KEY,
  RANKING_MC_VALUE_KEY,
  RANKING_MC_VALUE_PCT_KEY,
  RANKING_MC_VALUE_SUM_KEY,
  RANKING_MC_VALUE_SUM_LABEL,
  RANKING_MC_VALUE_SUM_PCT_KEY,
} from './dashboard-ranking-multi-column.definition';
import {
  RankingMultiColumnMetricProfile,
  RANKING_MC_CHART_MEASURES,
  RANKING_MC_MAP_MEASURES,
} from './dashboard-ranking-multi-column-metric.profile';
import { rankingChartHeadingLead, rankingMapHeadingLead } from '../dashboard-ranking/dashboard-ranking-metric.profile';
import { createIdNameTransformers, mapHeadingPhrase, mapLabelKeyOf } from '../../../../dashboard-config/granularities/granularity-profile';
import { kpiColumn } from '../../../../dashboard-config/kpis/kpi-columns';
import {
  KpiColumnDataTransformer,
  KpiExportDataTransformer,
  KpiLabelDataTransformer,
  KpiValueDisplayDataTransformer,
} from '../../../../dashboard-config/kpis/kpi-data-transformers';
import { createKpiFilters } from '../../../../dashboard-config/kpis/kpi-filters';
import {
  DashboardRankingMultiColumnShell,
  dashboardRankingMultiColumnShellProfiles,
} from './dashboard-ranking-multi-column-shell.profile';

export type CreateDashboardRankingMultiColumnDefinitionInput =
  CreateDashboardDefinitionBase<
    DashboardRankingMultiColumnShell,
    RankingMultiColumnMetricProfile
  >;

export function createDashboardRankingMultiColumnDefinition(
  input: CreateDashboardRankingMultiColumnDefinitionInput,
): DashboardRankingMultiColumnDefinition {
  const { axes, shell, metric } = resolveProduct(input, dashboardRankingMultiColumnShellProfiles);
  const ranking = metric.ranking;
  const granularityId = axes.granularity.id;
  const context = axes.kpiContext;

  /* ---- Time ---- */
  const time = resolveTimeFilter(axes, 'single');
  const { timeline } = timeFilterDetail(time);

  /* ---- Headings ---- */
  const chartHeadingLead = rankingChartHeadingLead(ranking);
  const mapHeadingLead = rankingMapHeadingLead(ranking);

  return defineDashboard<DashboardRankingMultiColumnDefinition>(axes, shell, {
    heading: (filterValues) =>
      composeHeading(
        [chartHeadingLead(filterValues), shell.chartHeadingGranularity(filterValues)],
        filterValues.get(timeline.filterName),
      ),
    mapHeading: (filterValues) =>
      composeHeading([mapHeadingLead(filterValues), mapHeadingPhrase(granularityId, filterValues)]),

    filters: (deps) => [
      ...createTimeFilters(deps, time),
      ...shell.buildGranularityFilters(deps),
      ...createKpiFilters(axes.metric, granularityId),
      createRouteMethodologyFilter(deps),
    ],

    preFilterTransformers: (deps) => [
      new UndefinedStateToSumGermanyDashboardDataTransformer(),
      ...createPeriodLabelTransformers(deps, axes),
    ],

    // Legacy multi-column order: period extras → ranks → labels → id maps → chart values → displays.
    postFilterTransformers: (deps) => [
      ...createPeriodYearTransformers(axes),
      ...axes.metric.kpis().map(
        (kpi) => new RankDashboardDataTransformer(kpiColumn(kpi, 'rank', context), kpiColumn(kpi, 'value', context)),
      ),
      // Legacy order: runs before the chart values below fill `value`.
      new RankDashboardDataTransformer(RANKING_MC_RANK_KEY, RANKING_MC_VALUE_KEY),
      new KpiLabelDataTransformer(),
      ...createIdNameTransformers(deps, granularityId),
      ...RANKING_MC_CHART_MEASURES.map(
        ({ measure, resultColumn }) => new KpiColumnDataTransformer({ measure, resultColumn, context }),
      ),
      ...[RANKING_MC_VALUE_KEY, RANKING_MC_VALUE_SUM_KEY].map(
        (valueKey) => new KpiValueDisplayDataTransformer({ valueColumnKey: valueKey, resultColumnKey: valueKey + '_display' }),
      ),
      new PercentageValueDataTransformer(RANKING_MC_VALUE_PCT_KEY, RANKING_MC_VALUE_PCT_KEY + '_display'),
      new PercentageValueDataTransformer(RANKING_MC_VALUE_SUM_PCT_KEY, RANKING_MC_VALUE_SUM_PCT_KEY + '_display'),
    ],

    exportTransformers: () => [new KpiExportDataTransformer({ kpis: axes.metric.kpis(), percentageMeasures: [] })],

    mapTransformers: () => [
      ...RANKING_MC_MAP_MEASURES.map(
        ({ measure, resultColumn }) => new KpiColumnDataTransformer({ measure, resultColumn, context }),
      ),
      new NumDEDashboardDataTransformer({ valueColumnKey: RANKING_MC_VALUE_KEY, resultColumnKey: RANKING_MC_VALUE_KEY + '_display' }),
      new PercentageValueDataTransformer(RANKING_MC_VALUE_PCT_KEY, RANKING_MC_VALUE_PCT_KEY + '_display'),
      new ChartFieldDashboardDataTransformer('value_label', (row) => row[mapLabelKeyOf(granularityId)]),
      new RankDashboardDataTransformer('rank', RANKING_MC_VALUE_KEY, { groupColumn: 'year', groupRankColumn: 'rank' }),
    ],

    view: {
      valueKey: RANKING_MC_VALUE_KEY,
      valuePctKey: RANKING_MC_VALUE_PCT_KEY,
      valueSumKey: RANKING_MC_VALUE_SUM_KEY,
      valueSumPctKey: RANKING_MC_VALUE_SUM_PCT_KEY,
      valueSumLabel: RANKING_MC_VALUE_SUM_LABEL,
      rankKey: RANKING_MC_RANK_KEY,
      metricLabel: axes.metric.labels.shortLabel,
      metricLabelKey: metric.metricLabelKey,
      ...axesView(axes),
      popup: {
        kEur: isKEur(axes),
        slot: ranking.popupSlot,
      },
    },
  });
}
